import { useCallback, useRef } from 'react'
import {
    getDeviceLocationStatus,
    putDeviceLocation,
} from '../api/client'

/**
 * Capture the browser's location for the check-in parse anchor — as rarely as
 * we can get away with.
 *
 * The constraint this is built around: `navigator.geolocation` prompts per
 * origin, and Safari re-prompts roughly per session. Asking on every parse
 * would be intolerable, so the fix is cached server-side and we only reach for
 * the browser once that cache goes stale. Everything below is a reason NOT to
 * call `getCurrentPosition`.
 *
 * `ensureFreshFix` resolves to true only when it actually stored a new fix.
 */

/** How stale the stored fix must be before we re-ask the browser. On Safari
 *  this is roughly the prompt rate; Chrome/Firefox/Edge grant persistently, so
 *  there it costs nothing but a cheap position read. */
export const REFRESH_AFTER_MS = 6 * 60 * 60 * 1000

/** A fix only speaks for a check-in about roughly the same day, matching the
 *  backend's tolerance. Beyond this we don't even ask — stale coordinates
 *  should never leave the device. */
const APPLIES_TO_TOLERANCE_MS = 36 * 60 * 60 * 1000

const GEO_TIMEOUT_MS = 8000
/** Let the browser hand back a recent cached position rather than powering up
 *  the radio. A 30km bias circle does not need a fresh GPS lock. */
const GEO_MAX_AGE_MS = 10 * 60 * 1000

export interface DeviceLocationCapture {
    /** Capture a fix if one is needed and permitted. Never throws. */
    ensureFreshFix: (appliesTo?: string | Date | null) => Promise<boolean>
}

function withinTolerance(appliesTo?: string | Date | null): boolean {
    if (!appliesTo) return true
    const when = appliesTo instanceof Date ? appliesTo : new Date(appliesTo)
    if (Number.isNaN(when.getTime())) return true
    return Math.abs(Date.now() - when.getTime()) <= APPLIES_TO_TOLERANCE_MS
}

async function permissionIsDenied(): Promise<boolean> {
    // `navigator.permissions` lets us know we'd be refused without triggering
    // anything. Not universally implemented; absence is not a denial.
    try {
        const perms = (navigator as Navigator & {
            permissions?: { query: (d: { name: string }) => Promise<{ state: string }> }
        }).permissions
        if (!perms?.query) return false
        const status = await perms.query({ name: 'geolocation' })
        return status.state === 'denied'
    } catch {
        return false
    }
}

function currentPosition(): Promise<GeolocationPosition | null> {
    return new Promise((resolve) => {
        const timer = window.setTimeout(() => resolve(null), GEO_TIMEOUT_MS)
        navigator.geolocation.getCurrentPosition(
            (pos) => { window.clearTimeout(timer); resolve(pos) },
            () => { window.clearTimeout(timer); resolve(null) },
            {
                enableHighAccuracy: false,
                timeout: GEO_TIMEOUT_MS,
                maximumAge: GEO_MAX_AGE_MS,
            },
        )
    })
}

/**
 * @param canPrompt Gate for the very first prompt — the caller passes false
 *   while the permission primer hasn't been accepted yet, so a cold browser
 *   dialog never appears unexplained. Once permission is already granted the
 *   caller can pass true; there is nothing left to pre-empt.
 */
export function useDeviceLocation(canPrompt: () => boolean): DeviceLocationCapture {
    // One capture at a time. Two composer saves landing together must not race
    // into two permission prompts.
    const inFlight = useRef<Promise<boolean> | null>(null)

    const ensureFreshFix = useCallback(
        async (appliesTo?: string | Date | null): Promise<boolean> => {
            if (inFlight.current) return inFlight.current

            const run = (async (): Promise<boolean> => {
                try {
                    // Geolocation is secure-context only. On the box this is
                    // what makes http://hgdev:5173 silently inert while
                    // the tailnet HTTPS origin works.
                    if (typeof window === 'undefined' || !window.isSecureContext) return false
                    if (!navigator.geolocation) return false
                    if (!withinTolerance(appliesTo)) return false
                    if (await permissionIsDenied()) return false

                    const status = await getDeviceLocationStatus()
                    if (status.opted_out) return false
                    if (status.captured_at) {
                        const age = Date.now() - new Date(status.captured_at).getTime()
                        if (age >= 0 && age < REFRESH_AFTER_MS) return false
                    }

                    if (!canPrompt()) return false

                    const pos = await currentPosition()
                    if (!pos) return false

                    await putDeviceLocation(
                        pos.coords.latitude,
                        pos.coords.longitude,
                        pos.coords.accuracy != null
                            ? Math.round(pos.coords.accuracy)
                            : undefined,
                    )
                    return true
                } catch {
                    // Location is an optional signal. A parse must never fail
                    // because we couldn't work out where the user is.
                    return false
                } finally {
                    inFlight.current = null
                }
            })()

            inFlight.current = run
            return run
        },
        [canPrompt],
    )

    return { ensureFreshFix }
}
